<template>
  <div class="recommendations-container">
    <Sidebar />
    
    <div class="header">
      <h1>Recommendations Management</h1>
      <div class="header-actions">
        <button @click="showCreateModal = true" class="create-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          Add New Recommendation
        </button>
      </div>
    </div>

    <div class="content">
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>Loading recommendations...</p>
      </div>

      <div v-else-if="error" class="error">
        <p>{{ error }}</p>
        <button @click="fetchRecommendations" class="retry-button">Retry</button>
      </div>

      <div v-else class="table-container">
        <div class="search-controls">
          <div class="search-container">
            <input 
              type="text" 
              v-model="search" 
              placeholder="Search..." 
              class="search-input"
              @input="onSearch"
            />
          </div>
          
          <div class="filter-controls">
            <div class="filter-select">
              <label>Persona:</label>
              <select v-model="personaFilter" @change="applyFilters">
                <option value="">All Personas</option>
                <option v-for="persona in personas" :key="persona.id" :value="persona.id">
                  {{ persona.name }}
                </option>
              </select>
            </div>
            
            <div class="filter-select">
              <label>Course:</label>
              <select v-model="courseFilter" @change="applyFilters">
                <option value="">All Courses</option>
                <option v-for="course in courses" :key="course.id" :value="course.id">
                  {{ course.title }}
                </option>
              </select>
            </div>
            
            <button v-if="hasActiveFilters" @click="clearFilters" class="clear-filters-btn">
              Clear Filters
            </button>
          </div>
          
          <div class="id-search-container">
            <input 
              type="number" 
              v-model="recommendationId" 
              placeholder="Search by ID..." 
              class="id-search-input"
            />
            <button @click="fetchRecommendationById" class="search-id-button">
              Get Recommendation
            </button>
            <button v-if="showingSpecificId" @click="clearSpecificSearch" class="clear-button">
              Show All
            </button>
          </div>
        </div>
        
        <!-- Data Table -->
        <table class="data-table">
          <thead>
            <tr>
              <th class="checkbox-column">
                <input 
                  type="checkbox" 
                  :checked="selectAll" 
                  @change="toggleSelectAll"
                  class="checkbox"
                />
              </th>
              <th>ID</th>
              <th>Persona</th>
              <th>Course</th>
              <th>Weight</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in recommendationsData" :key="item.id">
              <td class="checkbox-column">
                <input 
                  type="checkbox" 
                  :checked="isSelected(item.id)"
                  @change="toggleSelect(item.id)"
                  class="checkbox"
                />
              </td>
              <td>{{ item.id }}</td>
              <td>{{ item.persona_name }}</td>
              <td>{{ item.course_title }}</td>
              <td>{{ item.weight }}</td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td>{{ formatDate(item.updated_at) }}</td>
              <td>
                <div class="actions">
                  <button @click="openEditModal(item)" class="action-btn edit">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button @click="confirmDelete(item.id)" class="action-btn delete">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 6h18"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        
        <!-- Multi-select actions -->
        <div v-if="selectedItems.length > 0" class="multi-select-actions">
          <span>{{ selectedItems.length }} {{ selectedItems.length === 1 ? 'item' : 'items' }} selected</span>
          <button @click="confirmDeleteSelected" class="delete-selected-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Delete Selected
          </button>
        </div>
        
        <!-- Pagination controls -->
        <div class="custom-pagination" v-if="!showingSpecificId">
          <div class="pages-info">
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <span>Showing {{ recommendationsData.length }} of {{ totalItems }} items</span>
          </div>
          
          <div class="pagination-controls">
            <button 
              class="pagination-button" 
              :disabled="currentPage <= 1" 
              @click="changePage(currentPage - 1)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="pagination-icon">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
              Previous
            </button>
            
            <div class="page-numbers">
              <template v-for="page in displayedPages" :key="page">
                <button 
                  class="page-number" 
                  :class="{ active: page === currentPage }"
                  @click="changePage(page)"
                  v-if="typeof page === 'number'"
                >
                  {{ page }}
                </button>
                <span v-else class="ellipsis">{{ page }}</span>
              </template>
            </div>
            
            <button 
              class="pagination-button" 
              :disabled="currentPage >= totalPages" 
              @click="changePage(currentPage + 1)"
            >
              Next
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="pagination-icon">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
          
          <div class="rows-per-page">
            <span>Rows per page:</span>
            <select v-model="perPage" @change="onPerPageChange">
              <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <div v-if="showCreateModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Create New Recommendation</h2>
          <button @click="showCreateModal = false" class="close-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="form-group">
            <label>Persona</label>
            <div class="search-select-container">
              <input 
                v-model="personaSearch" 
                type="text" 
                placeholder="Search personas..." 
                class="search-dropdown-input"
              />
              <select v-model="createForm.persona_id" class="form-input" size="5">
                <option value="">Select a persona</option>
                <option v-for="persona in filteredPersonas" :key="persona.id" :value="persona.id">
                  {{ persona.name }}
                </option>
              </select>
            </div>
          </div>
          
          <div class="form-group">
            <label>Course</label>
            <div class="search-select-container">
              <input 
                v-model="courseSearch" 
                type="text" 
                placeholder="Search courses..." 
                class="search-dropdown-input"
              />
              <select v-model="createForm.course_id" class="form-input" size="5">
                <option value="">Select a course</option>
                <option v-for="course in filteredCourses" :key="course.id" :value="course.id">
                  {{ course.title }}
                </option>
              </select>
            </div>
          </div>
          
          <div class="form-group">
            <label>Weight</label>
            <input 
              v-model.number="createForm.weight" 
              type="number" 
              class="form-input"
              placeholder="Enter weight"
              min="1"
            >
          </div>

          <div class="modal-actions">
            <button 
              @click="showCreateModal = false"
              class="cancel-button"
            >
              Cancel
            </button>
            <button 
              @click="createRecommendation"
              :disabled="isSubmitting"
              class="submit-button"
            >
              {{ isSubmitting ? 'Creating...' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Edit Recommendation</h2>
          <button @click="showEditModal = false" class="close-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="form-group">
            <label>Persona</label>
            <select v-model="editForm.persona_id" class="form-input">
              <option value="">Select a persona</option>
              <option v-for="persona in personas" :key="persona.id" :value="persona.id">
                {{ persona.name }}
              </option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Course</label>
            <select v-model="editForm.course_id" class="form-input">
              <option value="">Select a course</option>
              <option v-for="course in courses" :key="course.id" :value="course.id">
                {{ course.title }}
              </option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Weight</label>
            <input 
              v-model.number="editForm.weight" 
              type="number" 
              class="form-input"
              placeholder="Enter weight"
              min="1"
            >
          </div>

          <div class="modal-actions">
            <button 
              @click="showEditModal = false"
              class="cancel-button"
            >
              Cancel
            </button>
            <button 
              @click="updateRecommendation"
              :disabled="isSubmitting"
              class="submit-button"
            >
              {{ isSubmitting ? 'Updating...' : 'Update' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_CONFIG, AUTH_CONFIG } from '@/config';
import Sidebar from '@/components/Sidebar.vue';

export default {
  name: 'RecommendationsManager',
  components: {
    Sidebar
  },
  data() {
    return {
      recommendationsData: [],
      loading: true,
      error: null,
      totalItems: 0,
      search: '',
      recommendationId: '',
      showingSpecificId: false,
      debounceTimeout: null,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      showCreateModal: false,
      showEditModal: false,
      isSubmitting: false,
      
      // Filters
      personaFilter: '',
      courseFilter: '',
      
      // Search for dropdowns
      personaSearch: '',
      courseSearch: '',
      
      // Multi-select related data
      selectedItems: [],
      selectAll: false,
      
      // Data for forms
      personas: [],
      courses: [],
      
      createForm: {
        persona_id: '',
        course_id: '',
        weight: 1
      },
      
      editForm: {
        id: null,
        persona_id: '',
        course_id: '',
        weight: 1
      }
    };
  },
  computed: {
    totalPages() {
      if (!this.totalItems || !this.perPage) return 1;
      return Math.ceil(this.totalItems / this.perPage);
    },
    displayedPages() {
      const pages = [];
      const totalPages = this.totalPages;
      const currentPage = this.currentPage;
      
      // Always show first page
      if (totalPages >= 1) {
        pages.push(1);
      }
      
      // Add ellipsis if needed
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        if (i !== 1 && i !== totalPages) { // Avoid duplicating first and last page
          pages.push(i);
        }
      }
      
      // Add ellipsis if needed
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page if it exists
      if (totalPages > 1) {
        pages.push(totalPages);
      }
      
      return pages;
    },
    hasActiveFilters() {
      return this.personaFilter !== '' || this.courseFilter !== '';
    },
    filteredPersonas() {
      if (!this.personaSearch) return this.personas;
      
      const searchTerm = this.personaSearch.toLowerCase();
      return this.personas.filter(persona => 
        persona.name.toLowerCase().includes(searchTerm)
      );
    },
    filteredCourses() {
      if (!this.courseSearch) return this.courses;
      
      const searchTerm = this.courseSearch.toLowerCase();
      return this.courses.filter(course => 
        course.title.toLowerCase().includes(searchTerm)
      );
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    // Multi-select methods
    isSelected(id) {
      return this.selectedItems.includes(id);
    },
    
    toggleSelect(id) {
      const index = this.selectedItems.indexOf(id);
      if (index === -1) {
        this.selectedItems.push(id);
      } else {
        this.selectedItems.splice(index, 1);
      }
      
      // Update selectAll state based on current selection
      this.selectAll = this.selectedItems.length === this.recommendationsData.length;
    },
    
    toggleSelectAll() {
      this.selectAll = !this.selectAll;
      
      if (this.selectAll) {
        // Select all items
        this.selectedItems = this.recommendationsData.map(item => item.id);
      } else {
        // Deselect all items
        this.selectedItems = [];
      }
    },
    
    confirmDeleteSelected() {
      if (confirm(`Are you sure you want to delete ${this.selectedItems.length} selected recommendation${this.selectedItems.length !== 1 ? 's' : ''}? This action cannot be undone.`)) {
        this.deleteSelectedRecommendations();
      }
    },
    
    async deleteSelectedRecommendations() {
      if (this.selectedItems.length === 0) return;
      
      this.loading = true;
      
      try {
        const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
        const deletePromises = this.selectedItems.map(id => 
          axios.delete(
            `${API_CONFIG.BASE_URL}/admin/recommendations/${id}`,
            {
              headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            }
          )
        );
        
        await Promise.all(deletePromises);
        
        // Reset selection and refresh data
        this.selectedItems = [];
        this.selectAll = false;
        await this.fetchRecommendations();
        
        // Show success message
        alert(`Successfully deleted ${deletePromises.length} recommendation${deletePromises.length !== 1 ? 's' : ''}`);
        
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to delete selected recommendations');
        console.error('API Error:', err);
      } finally {
        this.loading = false;
      }
    },
    
    // Fetch all recommendations
    async fetchRecommendations() {
      this.loading = true;
      this.showingSpecificId = false;
      
      try {
        const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
        
        const params = {
          page: this.currentPage,
          per_page: this.perPage
        };
        
        if (this.search) {
          params.search = this.search;
        }
        
        if (this.personaFilter) {
          params.persona_id = this.personaFilter;
        }
        
        if (this.courseFilter) {
          params.course_id = this.courseFilter;
        }
        
        const response = await axios.get(`${API_CONFIG.BASE_URL}/admin/recommendations`, {
          params,
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        this.recommendationsData = response.data.data;
        this.totalItems = response.data.total;
        this.currentPage = response.data.current_page;
        
      } catch (err) {
        this.error = err.message || 'Failed to fetch recommendations';
        console.error('API Error:', err);
      } finally {
        this.loading = false;
      }
    },
    
    // Fetch a single recommendation by ID
    async fetchRecommendationById() {
      if (!this.recommendationId) {
        this.error = 'Please enter a recommendation ID';
        return;
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
        
        const response = await axios.get(`${API_CONFIG.BASE_URL}/admin/recommendations/${this.recommendationId}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        // Check if we got a valid response with data
        if (response.data && response.data.data) {
          // If the response returns an array with one item
          if (Array.isArray(response.data.data)) {
            this.recommendationsData = response.data.data;
          } else {
            // If the response returns a single object
            this.recommendationsData = [response.data.data];
          }
          
          this.totalItems = 1;
          this.showingSpecificId = true;
          
        } else {
          this.error = 'No recommendation found with that ID';
          this.recommendationsData = [];
        }
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to fetch recommendation';
        console.error('API Error:', err);
        this.recommendationsData = [];
      } finally {
        this.loading = false;
      }
    },
    
    // Create a new recommendation
    async createRecommendation() {
      if (!this.createForm.persona_id || !this.createForm.course_id || !this.createForm.weight) {
        alert('Please fill in all fields');
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
        
        const response = await axios.post(
          `${API_CONFIG.BASE_URL}/admin/recommendations`,
          this.createForm,
          {
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (response.data) {
          // Close modal and reset form
          this.showCreateModal = false;
          this.resetCreateForm();
          
          // Refresh recommendations list
          await this.fetchRecommendations();
        }
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to create recommendation');
        console.error('API Error:', err);
      } finally {
        this.isSubmitting = false;
      }
    },
    
    // Update an existing recommendation
    async updateRecommendation() {
      if (!this.editForm.persona_id || !this.editForm.course_id || !this.editForm.weight) {
        alert('Please fill in all fields');
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
        
        const response = await axios.put(
          `${API_CONFIG.BASE_URL}/admin/recommendations/${this.editForm.id}`,
          {
            persona_id: this.editForm.persona_id,
            course_id: this.editForm.course_id,
            weight: this.editForm.weight
          },
          {
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (response.data) {
          // Close modal and reset form
          this.showEditModal = false;
          
          // Refresh recommendations list
          await this.fetchRecommendations();
        }
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to update recommendation');
        console.error('API Error:', err);
      } finally {
        this.isSubmitting = false;
      }
    },
    
    // Delete a recommendation
    async deleteRecommendation(id) {
      try {
        const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
        
        const response = await axios.delete(
          `${API_CONFIG.BASE_URL}/admin/recommendations/${id}`,
          {
            headers: {
              'Authorization': token ? `Bearer ${token}` : '',
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (response.data) {
          // Refresh recommendations list
          await this.fetchRecommendations();
        }
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to delete recommendation');
        console.error('API Error:', err);
      }
    },
    
    // Fetch all personas for dropdown
    async fetchPersonas() {
      try {
        const response = await axios.get('https://api.kewaar.com/api/personas/all/list', {
          headers: {
            'Authorization': 'Bearer 6179|jqJdzKjpICz4AJeeEtayyn8yoFISg6UdhKS6FTzRf7266f35',
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        if (response.data && response.data.data) {
          this.personas = response.data.data;
        }
      } catch (err) {
        console.error('Error fetching personas:', err);
      }
    },
    
    // Fetch all courses for dropdown
    async fetchCourses() {
      try {
        const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
        
        const response = await axios.get(`${API_CONFIG.BASE_URL}/admin/courses`, {
          params: { per_page: 100 }, // Get a larger list of courses
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        if (response.data && response.data.data) {
          this.courses = response.data.data;
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
      }
    },
    
    // Confirm before deleting
    confirmDelete(id) {
      if (confirm('Are you sure you want to delete this recommendation?')) {
        this.deleteRecommendation(id);
      }
    },
    
    // Open edit modal and populate form
    openEditModal(item) {
      this.editForm = {
        id: item.id,
        persona_id: item.persona_id,
        course_id: item.course_id,
        weight: item.weight
      };
      this.showEditModal = true;
    },
    
    // Reset create form
    resetCreateForm() {
      this.createForm = {
        persona_id: '',
        course_id: '',
        weight: 1
      };
      this.personaSearch = '';
      this.courseSearch = '';
    },
    
    // Clear search by ID
    clearSpecificSearch() {
      this.recommendationId = '';
      this.showingSpecificId = false;
      this.fetchRecommendations();
    },
    
    // Change page in pagination
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      
      this.currentPage = page;
      this.fetchRecommendations();
    },
    
    // Change rows per page
    onPerPageChange() {
      this.currentPage = 1; // Reset to first page
      this.fetchRecommendations();
    },
    
    // Apply filters
    applyFilters() {
      this.currentPage = 1; // Reset to first page when filtering
      this.fetchRecommendations();
    },
    
    // Clear all filters
    clearFilters() {
      this.personaFilter = '';
      this.courseFilter = '';
      this.currentPage = 1;
      this.fetchRecommendations();
    },
    
    // Search with debounce
    onSearch() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.currentPage = 1; // Reset to first page when searching
        this.fetchRecommendations();
      }, 300);
    }
  },
  async mounted() {
    // Fetch initial data
    await Promise.all([
      this.fetchRecommendations(),
      this.fetchPersonas(),
      this.fetchCourses()
    ]);
  }
};
</script>

<style scoped>
.recommendations-container {
  background-color: #f8fafc;
  padding: 2rem;
  padding-left: 300px; /* Increased padding for better sidebar spacing */
  min-height: 100vh;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.create-button {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.create-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);
}

.create-button svg {
  width: 1.25rem;
  height: 1.25rem;
}

.content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.loading {
  padding: 4rem;
  text-align: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  margin: 0 auto 1.5rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  padding: 2.5rem;
  text-align: center;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 8px;
  margin: 1rem;
}

.retry-button {
  margin-top: 1.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.retry-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);
}

.table-container {
  padding: 1.5rem;
}

.search-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.search-container {
  flex: 1;
  min-width: 250px;
}

.filter-controls {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  margin: 1rem 0;
  flex-wrap: wrap;
}

.filter-select {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.filter-select label {
  font-size: 0.95rem;
  font-weight: 500;
  color: #4b5563;
}

.filter-select select {
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: white;
  font-size: 0.95rem;
  min-width: 200px;
  transition: all 0.2s;
}

.filter-select select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.id-search-container {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.search-input, .id-search-input {
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.search-input {
  width: 100%;
  max-width: 350px;
}

.id-search-input {
  width: 180px;
}

.search-input:focus, .id-search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-id-button, .clear-button {
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.search-id-button {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.search-id-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);
}

.clear-button {
  background-color: #f3f4f6;
  color: #4b5563;
}

.clear-button:hover {
  background-color: #e5e7eb;
}

/* Data Table Styles */
.data-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.95rem;
}

.data-table th, .data-table td {
  padding: 1rem 1.25rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #4b5563;
  position: sticky;
  top: 0;
  z-index: 10;
}

.data-table tbody tr:hover {
  background-color: #f9fafb;
}

.actions {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn svg {
  width: 1.25rem;
  height: 1.25rem;
}

.action-btn.edit {
  background-color: #ecfdf5;
  color: #059669;
}

.action-btn.edit:hover {
  background-color: #d1fae5;
  transform: translateY(-1px);
}

.action-btn.delete {
  background-color: #fee2e2;
  color: #dc2626;
}

.action-btn.delete:hover {
  background-color: #fecaca;
  transform: translateY(-1px);
}

/* Custom pagination styles */
.custom-pagination {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 8px;
}

.pages-info {
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #4b5563;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.pagination-button {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.pagination-button:hover:not(:disabled) {
  background-color: #f3f4f6;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-icon {
  width: 1.25rem;
  height: 1.25rem;
  margin: 0 0.5rem;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.page-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e5e7eb;
  font-size: 0.95rem;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.page-number:hover:not(.active) {
  background-color: #f3f4f6;
  border-color: #d1d5db;
  transform: translateY(-1px);
}

.page-number.active {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-color: #3b82f6;
  color: white;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.ellipsis {
  margin: 0 0.5rem;
  color: #6b7280;
}

.rows-per-page {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: #4b5563;
}

.rows-per-page select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: white;
  font-size: 0.95rem;
  color: #4b5563;
  transition: all 0.2s;
}

.rows-per-page select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  backdrop-filter: blur(4px);
}

.modal-content {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.close-button {
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  transition: all 0.2s;
}

.close-button:hover {
  color: #4b5563;
  transform: rotate(90deg);
}

.close-button svg {
  width: 1.5rem;
  height: 1.5rem;
}

.modal-body {
  padding: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 500;
  color: #4b5563;
  font-size: 0.95rem;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.cancel-button, .submit-button {
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button {
  background-color: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
}

.cancel-button:hover {
  background-color: #e5e7eb;
  transform: translateY(-1px);
}

.submit-button {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive styles */
@media (max-width: 768px) {
  .recommendations-container {
    padding: 1.5rem;
    padding-left: 1.5rem;
  }
  
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .search-controls {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-container {
    width: 100%;
  }
  
  .id-search-container {
    width: 100%;
    flex-wrap: wrap;
  }
  
  .search-input {
    max-width: 100%;
  }
  
  .data-table {
    display: block;
    overflow-x: auto;
  }
  
  .pages-info {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .pagination-controls {
    flex-wrap: wrap;
  }
  
  .modal-content {
    width: 95%;
    max-width: 95%;
  }
}

.checkbox-column {
  width: 50px;
  text-align: center;
}

.checkbox {
  cursor: pointer;
  width: 20px;
  height: 20px;
}

.multi-select-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem;
  background-color: #f9f9fb;
  border-top: 1px solid #e5e7eb;
  margin-top: 1rem;
  border-radius: 0 0 12px 12px;
}

.delete-selected-button {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  background-color: #fee2e2;
  color: #dc2626;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.delete-selected-button:hover {
  background-color: #fecaca;
  transform: translateY(-1px);
}

.delete-selected-button svg {
  width: 1.25rem;
  height: 1.25rem;
}

.clear-filters-btn {
  padding: 0.75rem 1.25rem;
  background-color: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-filters-btn:hover {
  background-color: #e5e7eb;
  transform: translateY(-1px);
}

/* Search dropdown styles */
.search-select-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.search-dropdown-input {
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.search-dropdown-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
</style> 